export class AuthError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly statusCode: number,
    public readonly originalError?: unknown
  ) {
    super(message)
    this.name = "AuthError"
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthError)
    }
  }
}

export class ForbiddenError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly statusCode: number,
    public readonly originalError?: unknown
  ) {
    super(message)
    this.name = "ForbiddenError"
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ForbiddenError)
    }
  }
}

export function isForbiddenError(error: unknown): error is ForbiddenError {
  return error instanceof ForbiddenError
}

export class ClientError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly statusCode: number,
    public readonly originalError?: unknown
  ) {
    super(message)
    this.name = "ClientError"
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ClientError)
    }
  }
}

export class ServerError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly statusCode: number,
    public readonly originalError?: unknown
  ) {
    super(message)
    this.name = "ServerError"
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ServerError)
    }
  }
}

export class NetworkError extends Error {
  constructor(
    message: string,
    public readonly originalError?: unknown
  ) {
    super(message)
    this.name = "NetworkError"
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NetworkError)
    }
  }
}

export function parseApiError(response: unknown): string {
  if (typeof response === "object" && response !== null) {
    // Check for AdonisJS error format
    if ("errors" in response && Array.isArray((response as any).errors)) {
      const errors = (response as any).errors
      if (errors.length > 0 && errors[0].message) {
        return errors[0].message
      }
    }
    // Check for standard error format
    if ("message" in response && typeof (response as any).message === "string") {
      return (response as any).message
    }
    // Check for error array
    if ("error" in response && typeof (response as any).error === "string") {
      return (response as any).error
    }
  }
  return "An unknown error occurred"
}

export function isNetworkError(error: unknown): boolean {
  return (
    error instanceof NetworkError ||
    (error instanceof TypeError && error.message.includes("fetch")) ||
    (error instanceof Error && error.message.includes("network"))
  )
}

export function isAuthError(error: unknown): error is AuthError {
  return error instanceof AuthError
}

export function isClientError(error: unknown): error is ClientError {
  return error instanceof ClientError
}

export function isServerError(error: unknown): error is ServerError {
  return error instanceof ServerError
}

export function getUserFriendlyErrorMessage(error: unknown): string {
  if (error instanceof AuthError) {
    return error.message
  }
  if (error instanceof ForbiddenError) {
    return error.message
  }
  if (error instanceof ClientError) {
    return error.message
  }
  if (error instanceof ServerError) {
    return "Server error. Please try again later."
  }
  if (error instanceof NetworkError) {
    return "Network error. Please check your connection and try again."
  }
  if (error instanceof Error) {
    return error.message
  }
  return "An unexpected error occurred. Please try again."
}

export function getStandardRetryLogic() {
  return (failureCount: number, error: unknown): boolean => {
    if (error instanceof AuthError) {
      return false
    }
    if (error instanceof ForbiddenError) {
      return false
    }
    if (error instanceof ClientError) {
      return false
    }
    if (isServerError(error)) {
      return failureCount < 2
    }
    if (isNetworkError(error)) {
      return failureCount < 2
    }
    return false
  }
}

export function getStandardRetryDelay() {
  return (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 30000)
}
